import { Injectable, inject } from '@angular/core';
import {
  CanDeactivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import type { StudentTestSessionComponent } from '../../components/features/student/student-test-session/student-test-session.component';
import { TestTimerService } from '../services/utils/test-timer.service';
import { SwalService } from '../services/utils/swal.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class TestSessionGuard implements CanDeactivate<StudentTestSessionComponent> {
  private timerService = inject(TestTimerService);
  private swal = inject(SwalService);
  private translate = inject(TranslateService);

  async canDeactivate(
    component: StudentTestSessionComponent,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot,
  ): Promise<boolean> {
    // Allow deactivation if test is already submitted or exited intentionally
    if (component.isSubmitting || component.isExiting) {
      console.log('✅ Allowing navigation (submitted/exited)');
      return true;
    }

    // Check if navigating away from test session (not refreshing)
    if (nextState && !nextState.url.includes('/student/test/take/')) {
      console.log('🚪 Attempting to leave test session');

      // Confirm navigation away from test
      const confirmed = await this.swal.confirmAsync(
        this.translate.instant('TESTS.LEAVE_TEST_WARNING'),
      );

      if (confirmed) {
        // Clear the test state when intentionally leaving
        if (component.testId) {
          this.timerService.clearTestState(component.testId);
        }
        console.log('🗑️ Test state cleared - user left intentionally');
        return true;
      }

      // User cancelled navigation
      return false;
    }

    // Allow navigation within test or refresh
    console.log('✅ Allowing navigation (within test/refresh)');
    return true;
  }
}
